import React, { useState } from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { SingleDatePicker } from 'react-dates';
import { Field, Form as FinalForm } from 'react-final-form';

import {
  FieldDateRangeInput,
  FieldLocationAutocompleteInput,
  FieldTextInput,
  Form,
  H3,
  IconArrowHead,
  Modal,
  PrimaryButton,
} from '../../../components';
import * as validators from '../../../util/validators';
import {
  autocompletePlaceSelected,
  autocompleteSearchRequired,
  bookingDatesRequired,
  composeValidators,
  dateInstanceOfMoment,
  required,
} from '../../../util/validators';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
} from '../../../util/dates';

import css from './CreateTenderForm.module.css';

const DATE_FORMAT = 'D MMMM YYYY';

const identity = v => v;

const formatDate = date => moment(date).format(DATE_FORMAT);

const addSpace = value => value?.replace(/\n/g, '<br>');

const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

export const CreateTenderForm = props => {
  const {
    intl,
    formTitle,
    isPreview,
    onChangeModalState,
    onChangePreviewState,
    createTenderInProgress,
  } = props;

  const [focused, setFocused] = useState();
  const [focusedInput, setFocusedInput] = useState();
  const [touched, setTouched] = useState(false);

  const tenderReferenceNameLabel = intl.formatMessage({
    id: 'CreateTenderForm.tenderReferenceNameLabel',
  });
  const tenderReferenceNamePlaceholder = intl.formatMessage({
    id: 'CreateTenderForm.tenderReferenceNamePlaceholder',
  });

  const addressLabel = intl.formatMessage({
    id: 'CreateTenderForm.addressLabel',
  });
  const addressPlaceholder = intl.formatMessage({
    id: 'CreateTenderForm.addressPlaceholder',
  });

  const deadlineLabel = intl.formatMessage({
    id: 'CreateTenderForm.deadlineLabel',
  });
  const deadlinePlaceholder = intl.formatMessage({
    id: 'CreateTenderForm.deadlinePlaceholder',
  });

  const startDatePlaceholderText = intl.formatMessage({
    id: 'CreateTenderForm.startDatePlaceholderText',
  });
  const endDatePlaceholderText = intl.formatMessage({
    id: 'CreateTenderForm.endDatePlaceholderText',
  });

  const requirementsLabel = intl.formatMessage({
    id: 'CreateTenderForm.requirementsLabel',
  });
  const requirementsPlaceholder = intl.formatMessage({
    id: 'CreateTenderForm.requirementsPlaceholder',
  });

  const descriptionLabel = intl.formatMessage({
    id: 'CreateTenderForm.descriptionLabel',
  });
  const descriptionPlaceholder = intl.formatMessage({
    id: 'CreateTenderForm.descriptionPlaceholder',
  });

  const locationRequiredMessage = intl.formatMessage({
    id: 'CreateTenderForm.locationRequiredMessage',
  });
  const locationNotRecognizedMessage = intl.formatMessage({
    id: 'CreateTenderForm.locationNotRecognizedMessage',
  });

  const requiredMessage = intl.formatMessage({
    id: 'BrandDetailsForm.requiredMessage',
  });

  const invalidDateMessage = intl.formatMessage({
    id: 'CreateTenderForm.submissionDeadlineInvalidDateMessage',
  });

  const onFocusedInputChange = v => setFocusedInput(v);

  const format = v => {
    const { startDate, endDate } = v || {};
    const formattedStart = startDate
      ? timeOfDayFromTimeZoneToLocal(startDate, getDefaultTimeZoneOnBrowser())
      : startDate;
    const formattedEnd = endDate
      ? timeOfDayFromTimeZoneToLocal(endDate, getDefaultTimeZoneOnBrowser())
      : endDate;
    return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
  };

  const parse = v => {
    const { startDate, endDate } = v || {};
    const parsedStart = startDate
      ? getStartOf(
          timeOfDayFromLocalToTimeZone(startDate, getDefaultTimeZoneOnBrowser()),
          'day',
          getDefaultTimeZoneOnBrowser()
        )
      : startDate;
    const parsedEnd = endDate
      ? getStartOf(
          timeOfDayFromLocalToTimeZone(endDate, getDefaultTimeZoneOnBrowser()),
          'day',
          getDefaultTimeZoneOnBrowser()
        )
      : endDate;
    return v ? { startDate: parsedStart, endDate: parsedEnd } : v;
  };

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          handleSubmit,
          values,
          form,
          errors,
          isOpen,
          onManageDisableScrolling,
        } = fieldRenderProps;

        const disableSubmit = !isEmpty(errors);

        const {
          tenderReferenceName,
          location,
          submissionDeadline,
          tenderDates,
          requirements,
          description,
        } = values ?? {};

        const title = formTitle ? (
          formTitle
        ) : (
          <FormattedMessage id="DocumentsPage.getStartedText" />
        );

        const previewContent = (
          <div>
            <H3 as="h1" className={css.heading}>
              <FormattedMessage id="DocumentsPage.previewConfirmationText" />
            </H3>
            <div className={css.subtitle}>
              <FormattedMessage id="DocumentsPage.previewSubtitleText" />
            </div>
            <div className={css.section}>
              <label htmlFor="#" className={css.previewLabel}>
                {tenderReferenceNameLabel}
              </label>
              <div className={css.previewText}>{tenderReferenceName}</div>
            </div>
            <div className={css.section}>
              <label htmlFor="#" className={css.previewLabel}>
                {addressLabel}
              </label>
              <div className={css.previewText}>
                {location?.selectedPlace?.secondaryText || location?.search}
              </div>
            </div>
            <div className={css.section}>
              <label htmlFor="#" className={css.previewLabel}>
                {deadlineLabel}
              </label>
              <div className={css.previewText}>{formatDate(submissionDeadline?.date)}</div>
            </div>
            <div className={css.datesContainer}>
              <div>
                <label htmlFor="#" className={css.previewLabel}>
                  {intl.formatMessage({
                    id: 'BookingDatesForm.bookingStartTitle',
                  })}
                </label>
                <div className={css.previewText}>{formatDate(tenderDates?.startDate)}</div>
              </div>
              <div>
                <label htmlFor="#" className={css.previewLabel}>
                  {intl.formatMessage({
                    id: 'BookingDatesForm.bookingEndTitle',
                  })}
                </label>
                <div className={css.previewText}>{formatDate(tenderDates?.endDate)}</div>
              </div>
            </div>
            <div className={css.section}>
              <label htmlFor="#" className={css.previewLabel}>
                {requirementsLabel}
              </label>
              <div
                className={css.previewText}
                dangerouslySetInnerHTML={{ __html: addSpace(requirements) }}
              />
            </div>
            <div className={css.section}>
              <label htmlFor="#" className={css.previewLabel}>
                {descriptionLabel}
              </label>
              <div
                className={css.previewText}
                dangerouslySetInnerHTML={{ __html: addSpace(description) }}
              />
            </div>

            <PrimaryButton
              type="submit"
              className={css.btn}
              onClick={() => handleSubmit(values)}
              inProgress={createTenderInProgress}
            >
              <FormattedMessage id="CreateTenderForm.portTenderBtn" />
            </PrimaryButton>
          </div>
        );

        const formContent = (
          <Form onSubmit={handleSubmit}>
            <H3 as="h1" className={css.heading}>
              {title}
            </H3>
            <div className={css.subtitle}>
              <FormattedMessage id="DocumentsPage.getStartedSubtitleText" />
            </div>
            <FieldTextInput
              id="tenderReferenceName"
              name="tenderReferenceName"
              className={css.section}
              label={tenderReferenceNameLabel}
              placeholder={tenderReferenceNamePlaceholder}
              disabled
            />
            <FieldLocationAutocompleteInput
              rootClassName={css.locationAddress}
              iconClassName={css.locationAutocompleteInputIcon}
              predictionsClassName={css.predictionsRoot}
              name="location"
              label={addressLabel}
              placeholder={addressPlaceholder}
              useDefaultPredictions={false}
              format={identity}
              valueFromForm={values?.location}
              validate={composeValidators(
                autocompleteSearchRequired(locationRequiredMessage),
                autocompletePlaceSelected(locationNotRecognizedMessage)
              )}
            />
            <div className={css.inputRoot}>
              <label htmlFor="submissionDeadline">{deadlineLabel}</label>
              <Field
                component={SingleDatePicker}
                id="submissionDeadline"
                name="submissionDeadline"
                date={values?.submissionDeadline?.date}
                onDateChange={date => {
                  form.change('submissionDeadline', { date });
                }}
                displayFormat={DATE_FORMAT}
                focused={focused}
                onFocusChange={({ focused }) => {
                  setFocused(focused);
                  setTouched(true);
                }}
                placeholder={deadlinePlaceholder}
                numberOfMonths={1}
                validate={composeValidators(
                  required(requiredMessage),
                  dateInstanceOfMoment(invalidDateMessage)
                )}
                navPrev={<PrevIcon />}
                navNext={<NextIcon />}
              />
              {touched && errors['submissionDeadline'] && (
                <span className={css.error}>{errors['submissionDeadline']}</span>
              )}
            </div>
            <div className={css.section}>
              <FieldDateRangeInput
                className={css.bookingDates}
                name="tenderDates"
                isDaily={false}
                startDateId="startDate"
                startDateLabel={intl.formatMessage({
                  id: 'BookingDatesForm.bookingStartTitle',
                })}
                startDatePlaceholderText={startDatePlaceholderText}
                endDateId="endDate"
                endDateLabel={intl.formatMessage({
                  id: 'BookingDatesForm.bookingEndTitle',
                })}
                endDatePlaceholderText={endDatePlaceholderText}
                focusedInput={focusedInput}
                onFocusedInputChange={onFocusedInputChange}
                useMobileMargins
                isOutsideRange={() => {}}
                displayFormat={DATE_FORMAT}
                validate={composeValidators(
                  required(
                    intl.formatMessage({
                      id: 'BookingDatesForm.requiredDate',
                    })
                  ),
                  bookingDatesRequired(
                    intl.formatMessage({
                      id: 'FieldDateRangeInput.invalidStartDate',
                    }),
                    intl.formatMessage({
                      id: 'FieldDateRangeInput.invalidEndDate',
                    })
                  )
                )}
                format={format}
                parse={parse}
              />
            </div>
            <FieldTextInput
              type="textarea"
              id="requirements"
              name="requirements"
              label={requirementsLabel}
              placeholder={requirementsPlaceholder}
              className={css.section}
              validate={validators.required(requiredMessage)}
            />
            <FieldTextInput
              type="textarea"
              id="description"
              name="description"
              label={descriptionLabel}
              placeholder={descriptionPlaceholder}
              className={css.section}
              validate={validators.required(requiredMessage)}
            />
            <PrimaryButton
              onClick={() => onChangePreviewState(true)}
              className={css.btn}
              disabled={disableSubmit}
            >
              <FormattedMessage id="CreateTenderForm.portTenderBtn" />
            </PrimaryButton>
          </Form>
        );

        return (
          <Modal
            id="CreateTenderForm"
            usePortal
            isOpen={isOpen}
            onClose={() => {
              onChangeModalState(false);
              onChangePreviewState(false);
            }}
            onManageDisableScrolling={onManageDisableScrolling}
            closeButtonMessage={' '}
            onHandleBack={() => onChangePreviewState(false)}
            showBackButton={isPreview}
          >
            {isPreview ? previewContent : formContent}
          </Modal>
        );
      }}
    />
  );
};
